import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  BookOpen, 
  Music, 
  Video, 
  Moon, 
  Flame, 
  ArrowRight,
  Calendar,
  Check,
  Play,
  Download,
  Info,
  Sparkles,
  Heart,
  Target,
  Compass,
  User,
  Clock,
  Sun,
  Coffee,
  ChevronDown,
  ChevronUp,
  HelpCircle,
  Share2,
  X
} from 'lucide-react';
import ContentCard from '../components/common/ContentCard';
import { retreatContent } from '../data/retreatContent';

// Helper to get all days
const getAllDays = () => {
  const days = [];
  retreatContent.weeks.forEach(week => {
    week.days.forEach(day => {
      days.push({ ...day, weekNumber: week.number, pillar: week.pillar });
    });
  });
  return days;
};

// Pillar info for visual display
const pillarInfo = {
  1: { name: 'Relationship', icon: Heart, color: 'rose', range: '1-7' },
  2: { name: 'Identity', icon: User, color: 'amber', range: '8-18' },
  3: { name: 'Mission', icon: Target, color: 'teal', range: '19-25' },
  4: { name: 'Purpose', icon: Compass, color: 'violet', range: '26-30' }
};

const Dashboard = ({ 
  currentDay, 
  dailyNotes, 
  retreatStartDate,
  onSetStartDate 
}) => {
  const navigate = useNavigate();
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const allDays = getAllDays();
  const todayInfo = allDays.find(d => d.day === currentDay);
  const shareUrl = typeof window !== 'undefined' ? window.location.origin : '';
  
  // Calculate stats
  const completedDays = Object.keys(dailyNotes).filter(k => dailyNotes[k]?.trim()).length;
  const progressPercent = Math.round((completedDays / 30) * 100);
  
  // Calculate streak
  let streak = 0;
  for (let i = 1; i <= 30; i++) {
    if (dailyNotes[i]?.trim()) streak++;
    else break;
  }

  // Get current week/pillar
  const getCurrentWeek = () => {
    if (!currentDay) return 1;
    if (currentDay <= 7) return 1;
    if (currentDay <= 18) return 2;
    if (currentDay <= 25) return 3;
    return 4;
  };

  const hasStarted = currentDay && currentDay >= 1 && currentDay <= 30;

  // Handle PDF download
  const handleDownloadPDF = () => {
    // Open the RC Retreat Guides page
    window.open('https://www.regnumchristi.com/en/retreat-guides/', '_blank');
  };

  return (
    <div className="px-4 py-6 max-w-lg mx-auto space-y-6">
      {/* Quick Glance - Today's Scripture (Visible immediately without scrolling) */}
      {hasStarted && todayInfo && (
        <div 
          onClick={() => navigate(`/day/${currentDay}`)}
          className="bg-gradient-to-r from-amber-50 via-orange-50 to-yellow-50 rounded-xl p-4 border-2 border-amber-300 shadow-md cursor-pointer hover:shadow-lg transition-all"
          data-testid="quick-glance-card"
        >
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center flex-shrink-0">
              <span className="font-bold text-white text-lg">{currentDay}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-amber-700 font-bold uppercase tracking-wider mb-1 flex items-center gap-1">
                <Sparkles className="w-3 h-3" />
                Today's Scripture
              </p>
              <p className="font-heading text-[var(--primary)] font-semibold text-sm mb-1">{todayInfo.scripture}</p>
              <p className="text-xs text-gray-600 italic line-clamp-2">
                "{todayInfo.scriptureText?.substring(0, 80)}..."
              </p>
            </div>
            <ArrowRight className="w-5 h-5 text-amber-500 flex-shrink-0 mt-2" />
          </div>
        </div>
      )}

      {/* Hero Section with Gradient Background */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[var(--primary)] via-[var(--primary-dark)] to-[var(--primary-dark)] p-6 text-white">
        <div className="absolute top-0 right-0 w-40 h-40 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
        
        <div className="relative text-center">
          <div className="inline-flex items-center gap-2 bg-white/20 px-3 py-1 rounded-full text-xs font-medium mb-4">
            <Sparkles className="w-3 h-3" />
            Ignatian Spirituality
          </div>
          <img 
            src={retreatContent.logo}
            alt="Divine Mercy University"
            className="w-20 h-20 mx-auto mb-4 rounded-full shadow-lg border-2 border-white/30"
          />
          <h1 className="font-heading text-2xl font-bold mb-1">
            {retreatContent.title}
          </h1>
          <p className="text-white/80 text-sm">{retreatContent.subtitle}</p>
          
          {/* Spiritual Foundation Link */}
          <button
            onClick={() => navigate('/resources')}
            className="mt-4 inline-flex items-center gap-2 text-white/80 hover:text-white text-sm underline underline-offset-2 decoration-white/40 hover:decoration-white/80 transition-all"
          >
            <BookOpen className="w-4 h-4" />
            Learn about our Spiritual Foundation
          </button>
        </div>
      </div>

      {/* Welcome Video - Tony at Town Hall */}
      <div className="bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-100">
        <div className="p-4 bg-gradient-to-r from-[var(--primary)]/10 to-[var(--secondary)]/10 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[var(--primary)] flex items-center justify-center">
              <Video className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-[var(--primary)]">Welcome to Your Retreat</h3>
              <p className="text-xs text-gray-500">An introduction from Tony</p>
            </div>
          </div>
        </div>
        <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
          <iframe
            title="Tony at Town Hall - Retreat Overview"
            src="https://player.vimeo.com/video/1174380746?h=05b4c66c83"
            className="absolute top-0 left-0 w-full h-full"
            frameBorder="0"
            allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
            allowFullScreen
          />
        </div>
      </div>

      {/* Continue Retreat Card - Main CTA */}
      {hasStarted ? (
        <div 
          className="bg-gradient-to-r from-[var(--secondary)] to-[var(--secondary)] rounded-2xl p-5 text-white shadow-lg cursor-pointer hover:shadow-xl transition-all"
          onClick={() => navigate(`/day/${currentDay}`)}
          data-testid="continue-retreat-card"
        >
          <div className="flex items-center justify-between mb-3">
            <span className="text-white/90 text-sm font-medium flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Week {getCurrentWeek()} • {pillarInfo[getCurrentWeek()].name}
            </span>
            {streak > 0 && (
              <span className="flex items-center gap-1 bg-white/20 px-2 py-1 rounded-full text-xs">
                <Flame className="w-3 h-3 text-orange-200" />
                {streak} day streak
              </span>
            )}
          </div>
          <h2 className="font-heading text-xl font-bold mb-1">
            Day {currentDay}: {todayInfo?.title || 'Continue'}
          </h2>
          <p className="text-white/80 text-sm mb-4">{todayInfo?.scripture}</p>
          <div className="flex items-center justify-between">
            <span className="text-white/70 text-sm flex items-center gap-1">
              <Check className="w-4 h-4" />
              {completedDays}/30 completed
            </span>
            <button className="bg-white text-[var(--secondary)] px-5 py-2 rounded-full text-sm font-bold flex items-center gap-1 hover:bg-white/90 transition-colors shadow">
              Continue
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      ) : (
        <div 
          className="bg-gradient-to-r from-[var(--secondary)] to-[var(--secondary)] rounded-2xl p-6 text-white shadow-lg cursor-pointer hover:shadow-xl transition-all"
          onClick={onSetStartDate}
          data-testid="start-retreat-card"
        >
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
              <Play className="w-6 h-6 ml-1" />
            </div>
            <div>
              <h2 className="font-heading text-xl font-bold">Start Your Retreat</h2>
              <p className="text-white/80 text-sm">Begin your 30-day journey to flourishing</p>
            </div>
          </div>
          <button className="w-full bg-white text-[var(--primary)] px-4 py-3 rounded-full font-bold flex items-center justify-center gap-2 hover:bg-white/90 transition-colors shadow">
            <Calendar className="w-5 h-5" />
            Set Start Date
          </button>
        </div>
      )}

      {/* Progress Bar */}
      {hasStarted && (
        <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-gray-700">Your Progress</span>
            <span className="text-sm font-bold text-[var(--primary)]">{progressPercent}%</span>
          </div>
          <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-[var(--primary)] via-[var(--secondary)] to-[var(--primary)] rounded-full transition-all duration-500"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          {/* Pillar Progress Indicators */}
          <div className="flex justify-between mt-3">
            {[1, 2, 3, 4].map((week) => {
              const info = pillarInfo[week];
              const Icon = info.icon;
              const isActive = getCurrentWeek() === week;
              return (
                <div 
                  key={week}
                  className={`flex flex-col items-center ${isActive ? 'opacity-100' : 'opacity-50'}`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    isActive 
                      ? `bg-${info.color}-100 text-${info.color}-600`
                      : 'bg-gray-100 text-gray-400'
                  }`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <span className="text-[10px] mt-1 text-gray-500">{info.name[0]}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* How This Retreat Works - Collapsible Card */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        <button
          onClick={() => setShowHowItWorks(!showHowItWorks)}
          className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
          data-testid="how-it-works-toggle"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[var(--primary)] to-[var(--primary-dark)] flex items-center justify-center">
              <HelpCircle className="w-5 h-5 text-white" />
            </div>
            <div className="text-left">
              <h3 className="font-heading font-semibold text-[var(--primary)]">How This Retreat Works</h3>
              <p className="text-xs text-gray-500">Daily rhythm & time commitment</p>
            </div>
          </div>
          {showHowItWorks ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </button>
        
        {showHowItWorks && (
          <div className="px-4 pb-4 space-y-4 border-t border-gray-100 pt-4">
            {/* Time Commitment */}
            <div className="text-center">
              <span className="inline-flex items-center gap-2 bg-[var(--primary)] text-white px-4 py-2 rounded-full text-sm font-medium">
                <Clock className="w-4 h-4" />
                ~45 minutes per day
              </span>
            </div>
            
            {/* Daily Structure */}
            <div className="space-y-2">
              <div className="flex items-center gap-3 bg-amber-50 rounded-lg p-3 border border-amber-100">
                <Sun className="w-5 h-5 text-amber-600 flex-shrink-0" />
                <div className="flex-1">
                  <span className="font-medium text-amber-900 text-sm">Morning Prayer</span>
                  <span className="text-amber-600 text-xs ml-2">20-30 min</span>
                </div>
                <span className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full">Essential</span>
              </div>
              <div className="flex items-center gap-3 bg-blue-50 rounded-lg p-3 border border-blue-100">
                <Coffee className="w-5 h-5 text-blue-600 flex-shrink-0" />
                <div className="flex-1">
                  <span className="font-medium text-blue-900 text-sm">Midday Check-in</span>
                  <span className="text-blue-600 text-xs ml-2">5 min</span>
                </div>
                <span className="text-xs bg-blue-200 text-blue-800 px-2 py-0.5 rounded-full">Essential</span>
              </div>
              <div className="flex items-center gap-3 bg-indigo-50 rounded-lg p-3 border border-indigo-100">
                <Moon className="w-5 h-5 text-indigo-600 flex-shrink-0" />
                <div className="flex-1">
                  <span className="font-medium text-indigo-900 text-sm">Evening Examen</span>
                  <span className="text-indigo-600 text-xs ml-2">10-15 min</span>
                </div>
                <span className="text-xs bg-indigo-200 text-indigo-800 px-2 py-0.5 rounded-full">Essential</span>
              </div>
            </div>
            
            {/* The 4 Pillars Summary */}
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs font-bold text-gray-600 uppercase tracking-wider mb-2">The 4 Pillars (RIMP)</p>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-rose-500 flex items-center justify-center text-white text-[10px] font-bold">R</div>
                  <span className="text-gray-600">Relationship (Wk 1)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-amber-500 flex items-center justify-center text-white text-[10px] font-bold">I</div>
                  <span className="text-gray-600">Identity (Wk 2)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-teal-500 flex items-center justify-center text-white text-[10px] font-bold">M</div>
                  <span className="text-gray-600">Mission (Wk 3)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-violet-500 flex items-center justify-center text-white text-[10px] font-bold">P</div>
                  <span className="text-gray-600">Purpose (Wk 4)</span>
                </div>
              </div>
            </div>
            
            {/* Optional Extras */}
            <div className="bg-green-50 rounded-lg p-3 border border-green-100">
              <p className="text-xs text-green-800">
                <strong>Optional extras:</strong> Sacred music, video talks, self-discovery profile, cloud sync
              </p>
            </div>
            
            <button
              onClick={() => navigate('/about')}
              className="w-full text-sm text-[var(--primary)] font-medium flex items-center justify-center gap-1 hover:underline"
            >
              Learn more about this retreat
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Quick Access Grid - Enhanced Colors */}
      <div className="grid grid-cols-2 gap-3">
        <div 
          onClick={() => navigate('/journal')}
          className="bg-gradient-to-br from-rose-50 to-rose-100 rounded-xl p-4 border border-rose-200 cursor-pointer hover:shadow-md transition-all"
          data-testid="quick-access-journal"
        >
          <div className="w-10 h-10 rounded-full bg-rose-500 flex items-center justify-center mb-3">
            <BookOpen className="w-5 h-5 text-white" />
          </div>
          <h3 className="font-heading font-semibold text-rose-900">Journal</h3>
          <p className="text-rose-600 text-sm">{completedDays} entries</p>
        </div>
        
        <div 
          onClick={() => navigate('/reference/examen')}
          className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-xl p-4 border border-indigo-200 cursor-pointer hover:shadow-md transition-all"
          data-testid="quick-access-examen"
        >
          <div className="w-10 h-10 rounded-full bg-indigo-500 flex items-center justify-center mb-3">
            <Moon className="w-5 h-5 text-white" />
          </div>
          <h3 className="font-heading font-semibold text-indigo-900">Examen</h3>
          <p className="text-indigo-600 text-sm">Daily prayer</p>
        </div>
        
        <div 
          onClick={() => navigate('/music')}
          className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl p-4 border border-emerald-200 cursor-pointer hover:shadow-md transition-all"
          data-testid="quick-access-music"
        >
          <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center mb-3">
            <Music className="w-5 h-5 text-white" />
          </div>
          <h3 className="font-heading font-semibold text-emerald-900">Music</h3>
          <p className="text-emerald-600 text-sm">Prayer & meditation</p>
        </div>
        
        <div 
          onClick={() => navigate('/talks')}
          className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-4 border border-amber-200 cursor-pointer hover:shadow-md transition-all"
          data-testid="quick-access-talks"
        >
          <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center mb-3">
            <Video className="w-5 h-5 text-white" />
          </div>
          <h3 className="font-heading font-semibold text-amber-900">Talks</h3>
          <p className="text-amber-600 text-sm">Spiritual guidance</p>
        </div>
      </div>

      {/* Today's Scripture Preview */}
      {hasStarted && todayInfo && (
        <div className="bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 rounded-xl p-5 border border-amber-200 shadow-sm">
          <p className="text-xs text-amber-700 font-bold uppercase tracking-wider mb-2 flex items-center gap-1">
            <Sparkles className="w-3 h-3" />
            Today's Scripture
          </p>
          <p className="font-heading text-[var(--primary)] font-semibold text-lg mb-2">{todayInfo.scripture}</p>
          {todayInfo.scriptureText && (
            <p className="text-sm text-gray-600 italic leading-relaxed line-clamp-3">
              "{todayInfo.scriptureText.substring(0, 150)}..."
            </p>
          )}
          <button 
            onClick={() => navigate(`/day/${currentDay}`)}
            className="mt-4 text-sm bg-[var(--primary)] text-white px-4 py-2 rounded-full font-medium flex items-center gap-1 hover:bg-[var(--primary-dark)] transition-colors"
          >
            Read full passage
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Share the Retreat - QR Code (Always Visible) */}
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-4 border-2 border-amber-300">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
            <Share2 className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-heading font-semibold text-amber-900">Share This Retreat</h3>
            <p className="text-xs text-amber-600">Invite friends & family to join you</p>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          <img 
            src={`https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(shareUrl)}&bgcolor=FFFFFF&color=5D1F2F`}
            alt="QR Code to share"
            className="w-24 h-24 rounded-lg bg-white p-1 shadow-md"
          />
          <div className="flex-1">
            <p className="text-amber-700 text-sm mb-2">
              Scan to share or tap to copy link
            </p>
            <button
              onClick={() => {
                navigator.clipboard.writeText(shareUrl);
                alert('Link copied!');
              }}
              className="bg-amber-500 text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-amber-600 transition-colors"
            >
              Copy Link
            </button>
          </div>
        </div>
      </div>

      {/* PDF Download & About Section */}
      <div className="grid grid-cols-2 gap-3">
        <div 
          onClick={handleDownloadPDF}
          className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-4 border border-blue-200 cursor-pointer hover:shadow-md transition-all"
          data-testid="download-pdf"
        >
          <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center mb-3">
            <Download className="w-5 h-5 text-white" />
          </div>
          <h3 className="font-heading font-semibold text-blue-900">PDF Guides</h3>
          <p className="text-blue-600 text-xs">Download for offline</p>
        </div>
        
        <div 
          onClick={() => navigate('/about')}
          className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-4 border border-purple-200 cursor-pointer hover:shadow-md transition-all"
          data-testid="about-retreat"
        >
          <div className="w-10 h-10 rounded-full bg-purple-500 flex items-center justify-center mb-3">
            <Info className="w-5 h-5 text-white" />
          </div>
          <h3 className="font-heading font-semibold text-purple-900">About</h3>
          <p className="text-purple-600 text-xs">Learn more & share</p>
        </div>
      </div>

      {/* Reference Links */}
      <div className="space-y-2">
        <p className="text-xs text-gray-500 font-bold uppercase tracking-wider px-1">Deepen Your Understanding</p>
        <ContentCard
          title="The RIMP Way"
          subtitle="Relationship, Identity, Mission, Purpose"
          onClick={() => navigate('/reference/rimp')}
        />
        <ContentCard
          title="Discernment Pillars"
          subtitle="Ignatian wisdom for decision-making"
          onClick={() => navigate('/reference/discernment')}
        />
      </div>

      {/* Footer Quote */}
      <div className="text-center pt-4 border-t border-gray-100">
        <p className="text-xs text-gray-400 italic">
          "The glory of God is man fully alive." — St. Irenaeus
        </p>
      </div>
    </div>
  );
};

export default Dashboard;
